import { Controller } from "@hotwired/stimulus"

export default class UploadProgressController extends Controller {
  static targets = [
    "form", "fileInput", "submitBtn", "cancelBtn",
    "progressSection", "progressBar", "progressText",
    "statusText", "fileMeta"
  ]

  connect() {
    this.formTarget.addEventListener("submit", (e) => this.handleSubmit(e))
  }

  fileSelected() {
    const file = this.fileInputTarget.files[0]
    if (!file) {
      this.fileMetaTarget.textContent = ""
      return
    }
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
    this.fileMetaTarget.textContent = `${file.name} — ${sizeMB} MB`
  }

  handleSubmit(e) {
    e.preventDefault()

    const file = this.fileInputTarget.files[0]
    if (!file) {
      this.statusTextTarget.textContent = "Please select a file first."
      this.statusTextTarget.className = "upload-status upload-status-error"
      return
    }

    // Build FormData BEFORE disabling inputs (disabled fields are excluded from FormData)
    const formData = new FormData(this.formTarget)

    // Show progress UI, hide form controls
    this.progressSectionTarget.style.display = "block"
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Uploading..."
    this.cancelBtnTarget.style.display = "none"
    this.fileInputTarget.disabled = true

    this.statusTextTarget.textContent = "Uploading file to server... Please wait."
    this.statusTextTarget.className = "upload-status upload-status-active"

    const xhr = new XMLHttpRequest()
    this._xhr = xhr
    this._startTime = Date.now()

    xhr.upload.addEventListener("progress", (e) => this.onProgress(e))
    xhr.addEventListener("load", () => this.onComplete(xhr))
    xhr.addEventListener("error", () => this.onError())
    xhr.addEventListener("abort", () => this.onAbort())

    // Get the CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    xhr.open("POST", this.formTarget.action)
    xhr.setRequestHeader("X-CSRF-Token", csrfToken)
    // Accept HTML so Rails redirects work — we'll follow the redirect manually
    xhr.setRequestHeader("Accept", "text/html, application/xhtml+xml")
    xhr.send(formData)
  }

  onProgress(e) {
    if (!e.lengthComputable) return

    const pct = ((e.loaded / e.total) * 100).toFixed(1)
    const loadedMB = (e.loaded / (1024 * 1024)).toFixed(1)
    const totalMB = (e.total / (1024 * 1024)).toFixed(1)

    this.progressBarTarget.style.width = `${pct}%`
    this.progressTextTarget.textContent = `${loadedMB} MB / ${totalMB} MB (${pct}%)`

    // Calculate speed and ETA
    const elapsed = (Date.now() - this._startTime) / 1000
    if (elapsed > 1 && e.loaded > 0) {
      const speed = e.loaded / elapsed
      const remaining = (e.total - e.loaded) / speed
      const speedMB = (speed / (1024 * 1024)).toFixed(1)

      let etaStr
      if (remaining < 60) {
        etaStr = `${Math.ceil(remaining)}s`
      } else {
        etaStr = `${Math.floor(remaining / 60)}m ${Math.ceil(remaining % 60)}s`
      }

      this.statusTextTarget.textContent =
        `Uploading at ${speedMB} MB/s — about ${etaStr} remaining`
    }
  }

  onComplete(xhr) {
    if (xhr.status >= 200 && xhr.status < 400) {
      this.progressBarTarget.style.width = "100%"
      this.statusTextTarget.textContent = "Upload complete! Redirecting to column mapping..."
      this.statusTextTarget.className = "upload-status upload-status-success"

      // Follow the redirect from Rails
      const redirectUrl = xhr.responseURL
      if (redirectUrl) {
        window.location.href = redirectUrl
      } else {
        window.location.reload()
      }
    } else {
      // Parse error from response
      const parser = new DOMParser()
      const doc = parser.parseFromString(xhr.responseText, "text/html")
      const flash = doc.querySelector(".flash-alert")
      const errorMsg = flash ? flash.textContent.trim() : "Upload failed. Please try again."

      this.statusTextTarget.textContent = errorMsg
      this.statusTextTarget.className = "upload-status upload-status-error"
      this.resetForm()
    }
  }

  onError() {
    this.statusTextTarget.textContent =
      "Network error during upload. Please check your connection and try again."
    this.statusTextTarget.className = "upload-status upload-status-error"
    this.resetForm()
  }

  onAbort() {
    this.statusTextTarget.textContent = "Upload cancelled."
    this.statusTextTarget.className = "upload-status upload-status-error"
    this.resetForm()
  }

  cancelUpload() {
    if (this._xhr) this._xhr.abort()
  }

  resetForm() {
    this.submitBtnTarget.disabled = false
    this.submitBtnTarget.textContent = "Upload & Map Columns"
    this.cancelBtnTarget.style.display = ""
    this.fileInputTarget.disabled = false
    this.progressSectionTarget.style.display = "none"
    this.progressBarTarget.style.width = "0%"
    this.progressTextTarget.textContent = ""
  }
}
